import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, Paperclip } from "lucide-react";
import { api } from "../lib/api";
import { tk, bn, fmtDate } from "../lib/format";
import { PageHeader, Empty, Loader, Modal, Badge } from "../components/kit";
import FileThumb from "../components/FileThumb";

const ST: Record<string, [string, string]> = { paid: ["পরিশোধিত", "green"], partial: ["আংশিক", "amber"], due: ["বাকি", "red"] };

function Detail({ p }: { p: any }) {
  return (
    <div className="space-y-4" data-testid="purchase-detail">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div><p className="text-slate-500">কোম্পানি</p><b>{p.company_name}</b></div>
        <div><p className="text-slate-500">চালান নং</p><b>{p.challan_no || "—"}</b></div>
        <div><p className="text-slate-500">তারিখ</p><b className="num">{fmtDate(p.date)}</b></div>
        <div><p className="text-slate-500">অবস্থা</p><Badge tone={ST[p.status][1]}>{ST[p.status][0]}</Badge></div>
      </div>
      <div className="overflow-x-auto">
        <table className="tbl">
          <thead><tr><th>পণ্য</th><th className="text-right">পরিমাণ</th><th className="text-right">ক্রয় দর</th><th className="text-right">মোট</th></tr></thead>
          <tbody>{p.items.map((it: any, i: number) => (<tr key={i}><td>{it.name}{!it.product_id && <span className="text-xs text-slate-400"> (ম্যানুয়াল)</span>}</td><td className="text-right num">{bn(it.qty)} {it.unit}</td><td className="text-right num">{tk(it.rate)}</td><td className="text-right num font-semibold">{tk(it.total)}</td></tr>))}</tbody>
        </table>
      </div>
      <div className="flex flex-wrap justify-end gap-6 text-sm">
        <span>মোট: <b className="num">{tk(p.total)}</b></span>
        <span>পরিশোধ: <b className="num text-emerald-700">{tk(p.paid)}</b></span>
        <span>বাকি: <b className="num text-red-600">{tk(p.due)}</b></span>
      </div>
      {p.note && <p className="text-sm bg-slate-50 rounded-lg p-3">নোট: {p.note}</p>}
      <div>
        <p className="font-semibold mb-2">চালানের কপি</p>
        {p.files?.length ? <div className="flex flex-wrap gap-3">{p.files.map((f: any) => <FileThumb key={f.id} file={f} />)}</div> : <p className="text-sm text-slate-400">কোনো ফাইল নেই</p>}
      </div>
    </div>
  );
}

export default function Purchases() {
  const [list, setList] = useState<any[] | null>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [cid, setCid] = useState("");
  const [view, setView] = useState<any>(null);

  useEffect(() => { api.get("/companies").then((r) => setCompanies(r.data)); }, []);
  useEffect(() => { api.get("/purchases", { params: { company_id: cid } }).then((r) => setList(r.data)); }, [cid]);

  return (
    <div>
      <PageHeader title="ক্রয় ইতিহাস" subtitle="কোম্পানি থেকে আসা সব মাল ও চালান"
        actions={<Link to="/purchase/new" className="btn btn-primary" data-testid="purchases-new-button"><Truck size={17} /> নতুন ক্রয়</Link>} />
      <div className="card p-4 mb-4">
        <select className="inp sm:w-72" value={cid} onChange={(e) => setCid(e.target.value)} data-testid="purchases-company-filter">
          <option value="">সব কোম্পানি</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="card overflow-x-auto">
        {!list ? <Loader /> : list.length === 0 ? <Empty text="কোনো ক্রয় এন্ট্রি নেই" /> : (
          <table className="tbl" data-testid="purchases-table">
            <thead><tr><th>তারিখ</th><th>এন্ট্রি নং</th><th>কোম্পানি</th><th>চালান</th><th className="text-right">মোট</th><th className="text-right">পরিশোধ</th><th className="text-right">বাকি</th><th>অবস্থা</th><th /></tr></thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} className="cursor-pointer" onClick={() => setView(p)} data-testid={`purchase-row-${p.purchase_no}`}>
                  <td className="num">{fmtDate(p.date)}</td>
                  <td className="font-semibold text-blue-700">{p.purchase_no}</td>
                  <td>{p.company_name}</td>
                  <td>{p.challan_no || "—"}</td>
                  <td className="text-right num font-semibold">{tk(p.total)}</td>
                  <td className="text-right num text-emerald-700">{tk(p.paid)}</td>
                  <td className="text-right num text-red-600">{tk(p.due)}</td>
                  <td><Badge tone={ST[p.status][1]}>{ST[p.status][0]}</Badge></td>
                  <td>{p.files?.length > 0 && <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Paperclip size={13} />{bn(p.files.length)}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <Modal open={!!view} onClose={() => setView(null)} title={`ক্রয় ${view?.purchase_no || ""}`} wide testid="purchase-detail-modal">{view && <Detail p={view} />}</Modal>
    </div>
  );
}
